// src/components/roads/ViewRoadModal.js
// --- UPDATE: v2.0 — AI Road Helper + explanations
//   • AI Road Helper panel: AI-readiness score, which AI features this road can
//     use (remaining-life, flood/hurricane, GIS, clustering, budget), what data
//     is still missing and why, and AI insights (remaining life, overdue
//     inspections, flood exposure from elevation, status/condition mismatches)
//   • (?) explainers on every metric and property — what it is, the scale, why
//     FlowSense needs it
//   • RUL colours match the network bands (≤2 red · ≤5 orange · ≤8 yellow)
//   • Mapping status no longer shows "mapped" for an empty "[]" polyline string
//   • Dark-mode fixes (hard-coded gray/cyan text), scrollable body, wider modal
// --- END UPDATE
// --- UPDATE: v2.1 — explainable AI prediction card (RUL source, risk index + drivers,
//   recommended actions, what the AI looks at, per-road model insights).
//   Optional prop onAiAction(intent, road) makes the action buttons clickable.
// --- END UPDATE
// --- UPDATE: v2.2 — "Last Paved" shows the saved install / last paved date (install_date column,
//   falling back to the older metadata.last_paved_date); criticality badge in the header.
// --- END UPDATE
import React, { useMemo } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, Box, Text, HStack, VStack, Badge, SimpleGrid, Icon, Divider, useColorModeValue
} from '@chakra-ui/react';
import { 
  MdVisibility, MdLocationOn, MdStraighten, MdAssessment, 
  MdEngineering, MdOutlineAutoAwesome, MdSchedule, MdDateRange, MdUpdate 
} from 'react-icons/md';
import { FaRoad } from 'react-icons/fa';
// --- UPDATE
import AiRoadHelper, { FieldHint, normalizeRoad } from './AiRoadHelper';
// --- END UPDATE
// --- UPDATE: v2.1
import { RoadAiPredictionDetail } from './RoadAiPrediction';
import { getRoadPrediction } from './roadAiPredictions';
// --- END UPDATE

const ViewRoadModal = ({ isOpen, onClose, road, /* --- UPDATE */ onAiAction /* --- END UPDATE */ }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const headerBg = useColorModeValue('white', 'gray.800');
  const bodyBg = useColorModeValue('white', 'gray.800');
  const roadNameColor = useColorModeValue('blue.700', 'blue.300');
  const roadCodeBg = useColorModeValue('white', 'gray.800');
  const footerBg = useColorModeValue('gray.50', 'gray.800');
  const timelineBg = useColorModeValue('gray.50', 'gray.700');
  const upcomingInspBg = useColorModeValue('cyan.50', 'cyan.900');
  // --- UPDATE: hoisted theme values for previously hard-coded colours
  const locationText = useColorModeValue('gray.600', 'gray.300');
  const upcomingInspText = useColorModeValue('cyan.700', 'cyan.200');
  const upcomingInspLabel = useColorModeValue('cyan.600', 'cyan.300');
  const upcomingInspBorder = useColorModeValue('cyan.200', 'cyan.700');
  const normalized = useMemo(() => (road ? normalizeRoad(road) : null), [road]);
  // --- END UPDATE
  // --- UPDATE: v2.1
  const prediction = useMemo(() => (road ? getRoadPrediction(road) : null), [road]);
  // --- END UPDATE

  const getConditionColor = (rating) => {
    const map = { excellent: 'green', good: 'teal', fair: 'yellow', poor: 'orange', critical: 'red' };
    return map[rating?.toLowerCase()] || 'gray';
  };

  const getStatusColor = (status) => {
    const map = { operational: 'green', maintenance_required: 'orange', under_maintenance: 'blue', closed: 'red', planned: 'purple' };
    return map[status?.toLowerCase()] || 'gray';
  };

  if (!road) return null;

  // --- UPDATE: RUL bands shared with the Road Works page / heatmap
  const rulYears = road.ai_rul?.predicted_rul_years;
  const rulScheme = rulYears == null ? 'gray' : rulYears <= 2 ? 'red' : rulYears <= 5 ? 'orange' : rulYears <= 8 ? 'yellow' : 'green';
  const nextDue = road.metadata?.next_inspection_due ? new Date(road.metadata.next_inspection_due) : null;
  const inspectionOverdue = nextDue && nextDue.getTime() < Date.now();
  // --- END UPDATE

  const openWOCount = road.open_work_orders_count || 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
          <HStack>
            <Box bg="blue.500" p={2.5} borderRadius="xl" boxShadow="sm">
              <Icon as={MdVisibility} color="white" boxSize={5} />
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="900">Asset Profile</Text>
              <Text fontSize="xs" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="wider">
                FlowSense Network Inventory
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        
        <ModalBody py={5} bg={bodyBg}>
          <VStack spacing={6} align="stretch">
            
            {/* Core Details Header Card */}
            <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden">
              <Box position="absolute" top={0} left={0} bottom={0} w="4px" bg={getStatusColor(road.status) + '.400'} />
              <HStack justify="space-between" align="flex-start" mb={2}>
                <Box>
                  <Text fontSize="2xl" fontWeight="900" color={roadNameColor} lineHeight="1.2">
                    {road.name || 'Unnamed Road'}
                  </Text>
                  <HStack mt={1} spacing={3}>
                    <Text fontSize="sm" fontFamily="mono" fontWeight="700" color={mutedColor} bg={roadCodeBg} px={2} py={0.5} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                      {road.metadata?.road_code || 'NO-CODE'}
                    </Text>
                    <Badge colorScheme={getStatusColor(road.status)} borderRadius="full" px={3} py={0.5} fontSize="xs">
                      {road.status ? road.status.replace(/_/g, ' ') : 'Unknown'}
                    </Badge>
                    {/* --- UPDATE: v2.2 */}
                    {road.asset_criticality && (
                      <Badge colorScheme={{ low: 'gray', medium: 'blue', high: 'orange', critical: 'red' }[String(road.asset_criticality).toLowerCase()] || 'gray'} variant="outline" borderRadius="full" px={3} py={0.5} fontSize="xs">
                        {String(road.asset_criticality)} criticality
                      </Badge>
                    )}
                    {/* --- END UPDATE */}
                  </HStack>
                </Box>
              </HStack>
              
              <HStack spacing={2} mt={4}>
                <Icon as={MdLocationOn} color="gray.400" boxSize={4} />
                <Text fontSize="sm" color={locationText} fontWeight="500">
                  {road.location_description || 'No location description provided.'}
                </Text>
              </HStack>
            </Box>

            {/* --- UPDATE: v2.1 AI prediction */}
            <RoadAiPredictionDetail prediction={prediction}
              onAction={onAiAction ? (intent) => onAiAction(intent, road) : undefined} />
            {/* --- END UPDATE */}

            {/* --- UPDATE: AI Road Helper */}
            <AiRoadHelper road={road} context="view" variant="full" defaultOpen />
            {/* --- END UPDATE */}

            {/* AI & Health Metrics */}
            <Box>
              <Text fontSize="xs" color={mutedColor} fontWeight="800" textTransform="uppercase" letterSpacing="wider" mb={3}>
                Asset Health & Diagnostics
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {/* Condition */}
                <Box p={3} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase" mb={1}>Current Condition<FieldHint metric="condition_score" /></Text>
                  <HStack>
                    <Icon as={MdAssessment} color={getConditionColor(road.metadata?.condition_rating) + '.500'} boxSize={4} />
                    <Badge colorScheme={getConditionColor(road.metadata?.condition_rating)} borderRadius="md">
                      {road.metadata?.condition_rating ? road.metadata.condition_rating.toUpperCase() : 'UNRATED'}
                    </Badge>
                    {road.metadata?.condition_score != null && (
                      <Text fontSize="xs" color={mutedColor} fontFamily="mono" fontWeight="bold">
                        ({road.metadata.condition_score}/100)
                      </Text>
                    )}
                  </HStack>
                </Box>

                {/* AI RUL */}
                <Box p={3} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase" mb={1}>AI Predicted RUL<FieldHint metric="rul" /></Text>
                  {road.ai_rul?.predicted_rul_years != null ? (
                    <Badge colorScheme={rulScheme} borderRadius="md" px={2} py={0.5} display="inline-flex" alignItems="center">
                      <Icon as={MdOutlineAutoAwesome} mr={1.5} />
                      {road.ai_rul.predicted_rul_years} Years
                    </Badge>
                  ) : <Text fontSize="sm" fontWeight="600" color="gray.400">—</Text>}
                </Box>

                {/* Work Orders */}
                <Box p={3} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase" mb={1}>Active Work Orders<FieldHint metric="work_orders" /></Text>
                  {openWOCount > 0 ? (
                    <Badge colorScheme="purple" borderRadius="md" px={2} py={0.5} display="inline-flex" alignItems="center">
                      <Icon as={MdEngineering} mr={1.5} />
                      {openWOCount} Pending
                    </Badge>
                  ) : <Text fontSize="sm" fontWeight="600" color="green.500">None Active</Text>}
                </Box>
              </SimpleGrid>
            </Box>

            {/* Physical Properties Grid */}
            <Box>
              <Text fontSize="xs" color={mutedColor} fontWeight="800" textTransform="uppercase" letterSpacing="wider" mb={3}>
                Physical Properties
              </Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                <Box>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase">Constituency<FieldHint field="constituency" /></Text>
                  <Text fontSize="sm" fontWeight="700">{road.metadata?.constituency || 'Unassigned'}</Text>
                </Box>
                <Box>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase">Surface Type<FieldHint field="surface_type" /></Text>
                  <HStack spacing={1.5}>
                    <Icon as={FaRoad} color="gray.400" boxSize={3} />
                    <Text fontSize="sm" fontWeight="700" textTransform="capitalize">
                      {road.metadata?.surface_type ? road.metadata.surface_type.replace(/_/g, ' ') : 'Unknown'}
                    </Text>
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase">Length<FieldHint field="length_m" /></Text>
                  <HStack spacing={1.5}>
                    <Icon as={MdStraighten} color="gray.400" boxSize={3.5} />
                    <Text fontSize="sm" fontWeight="700" fontFamily="mono">
                      {road.metadata?.length_m ? `${parseFloat(road.metadata.length_m).toFixed(1)} m` : '—'}
                    </Text>
                  </HStack>
                </Box>
                <Box>
                  <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase">Surveyed Elevation<FieldHint field="elevation" /></Text>
                  <Text fontSize="sm" fontWeight="700" fontFamily="mono" color="teal.500">
                    {road.elevation != null ? `${Number(road.elevation).toFixed(2)} m` : 'Unsurveyed'}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Lifecycle Timeline */}
            <Box>
              <Text fontSize="xs" color={mutedColor} fontWeight="800" textTransform="uppercase" letterSpacing="wider" mb={3}>
                Lifecycle Timeline
              </Text>
              <SimpleGrid columns={3} spacing={4}>
                <Box p={3} borderRadius="lg" bg={timelineBg}>
                  <HStack spacing={1.5} mb={1}>
                    <Icon as={MdUpdate} color="gray.400" boxSize={3.5} />
                    <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase">Last Paved<FieldHint field="install_date" /></Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">
                    {/* --- UPDATE: v2.2 install_date first; date-only strings parsed as local dates (no day shift) */}
                    {(() => { const v = road.install_date || road.metadata?.last_paved_date; if (!v) return 'Unknown'; const s = String(v); const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s); return Number.isNaN(d.getTime()) ? 'Unknown' : d.toLocaleDateString(); })()}
                  </Text>
                </Box>
                <Box p={3} borderRadius="lg" bg={timelineBg}>
                  <HStack spacing={1.5} mb={1}>
                    <Icon as={MdDateRange} color="gray.400" boxSize={3.5} />
                    <Text fontSize="2xs" color={mutedColor} fontWeight="700" textTransform="uppercase">Last Inspected</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="600">
                    {road.metadata?.last_inspection_date ? new Date(road.metadata.last_inspection_date).toLocaleDateString() : 'Unknown'}
                  </Text>
                </Box>
                <Box p={3} borderRadius="lg" bg={upcomingInspBg} borderWidth="1px" borderColor={inspectionOverdue ? 'red.300' : upcomingInspBorder}>
                  <HStack spacing={1.5} mb={1}>
                    <Icon as={MdSchedule} color={inspectionOverdue ? 'red.400' : 'cyan.500'} boxSize={3.5} />
                    <Text fontSize="2xs" color={upcomingInspLabel} fontWeight="800" textTransform="uppercase">Next Insp. Due<FieldHint field="inspection" /></Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="700" color={inspectionOverdue ? 'red.400' : upcomingInspText}>
                    {road.metadata?.next_inspection_due ? new Date(road.metadata.next_inspection_due).toLocaleDateString() : 'Unscheduled'}
                    {/* --- UPDATE */}
                    {inspectionOverdue && <Badge ml={2} colorScheme="red" fontSize="9px">Overdue</Badge>}
                    {/* --- END UPDATE */}
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Geometry Status */}
            <Divider borderColor={borderColor} />
            <HStack justify="space-between">
              <Text fontSize="xs" color={mutedColor} fontWeight="600" textTransform="uppercase">
                Mapping Status<FieldHint metric="mapping" />
              </Text>
              {/* --- UPDATE: parsed geometry (an empty "[]" string used to count as mapped) */}
              {normalized?.hasGeometry ? (
                <Badge colorScheme="green" variant="subtle" borderRadius="full" px={2}>
                  ✓ Spatially mapped on GIS
                </Badge>
              ) : (
                <Badge colorScheme="orange" variant="subtle" borderRadius="full" px={2}>
                  ⚠ Pending GIS mapping
                </Badge>
              )}
            </HStack>

          </VStack>
        </ModalBody>
        
        <ModalFooter borderTopWidth="1px" borderColor={borderColor} bg={footerBg}>
          <Button variant="outline" onClick={onClose} borderRadius="xl" size="sm">
            Close Profile
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ViewRoadModal;